/**
 * @viewport both
 * Public unsubscribe / register landing from marketing emails.
 */
import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Container,
  Paper,
  Typography,
  CircularProgress,
} from '@mui/material';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import api from '@/core/api/api';

export default function MarketingPreferencesPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [loading, setLoading] = useState(true);
  const [prefs, setPrefs] = useState(null);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('This link is invalid.');
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const res = await api.get('/public/marketing/preferences', { params: { token } });
        setPrefs(res.data?.data || res.data);
      } catch (e) {
        setError(e?.response?.data?.message || 'This link is invalid or has expired.');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const handleUnsubscribe = async () => {
    setError(null);
    try {
      await api.post('/public/marketing/unsubscribe', { token });
      setDone(true);
      setPrefs((p) => (p ? { ...p, subscribed: false } : p));
    } catch (e) {
      setError(e?.response?.data?.message || 'Unable to unsubscribe.');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Typography variant="h5" sx={{ mb: 2 }}>Email preferences</Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {prefs && !error && (
          <>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Invitation for: <strong>{prefs.email_masked}</strong>
              {prefs.audience_label ? ` (${prefs.audience_label})` : ''}
            </Typography>

            {done || !prefs.subscribed ? (
              <Alert severity="success" sx={{ mb: 2 }}>
                You will not receive further invitation emails from HFR Connect.
              </Alert>
            ) : (
              <Button variant="outlined" color="warning" onClick={handleUnsubscribe} sx={{ mb: 2 }}>
                Unsubscribe from invitations
              </Button>
            )}

            {prefs.register_url && (
              <Button
                component={RouterLink}
                to={prefs.register_url}
                variant="contained"
                fullWidth
              >
                Register on HFR Connect
              </Button>
            )}
          </>
        )}
      </Paper>
    </Container>
  );
}
